// Temporary geographic review map; owner model points remain proposals until validated.
import { L } from "./vendor.js";
import { html, safeUrl } from "./html.js";
import "../css/port-verification.css";

const position = (p) => `${Number(p.latitude).toFixed(5)}, ${Number(p.longitude).toFixed(5)}`;
let editingActive = false;
export const isPortVerificationEditing = () => editingActive;

export function verificationPopup(port, kind = "port") {
  const point = kind === "sampling" ? port.sampling : kind === "target" ? port.target : kind === "proposed" ? port.proposed : kind === "harbour" ? port.harbour : port;
  return html`<article class="port-verification-popup">
    <strong>${port.name}</strong><p>${port.waterBody} · ${port.region}</p>
    <p>${kind === "sampling" ? "Model cell" : kind === "proposed" ? "Proposed model point; data coverage unverified" : kind === "target" ? "Calculated offshore target" : kind === "harbour" ? "Fixed port reference" : port.locationType === "bay" ? "Bay map reference" : "Fixed location reference"}<br>${position(point)}</p>
    ${kind === "port" ? html`<p>${port.source?.startsWith("https://") ? html`<a href="${safeUrl(port.source)}" target="_blank" rel="noopener">Location source</a>` : port.source}</p>` : ""}
    ${port.approved ? html`<p>${port.sampling.model} · Model depth ${Number(port.depthMeters).toFixed(1)} m${port.userSelectedRemote ? ` · Distant ${port.samplingWaterBody} cell` : ""}</p>` : html`<p>${port.issue}</p>`}
    <a href="${port.url}">Open fishing conditions</a>
  </article>`;
}

export function setupPortVerification(map) {
  const toggle = document.querySelector("[data-port-verification]");
  if (!toggle) return;
  const status = document.querySelector("[data-port-verification-status]");
  const editor = document.querySelector("[data-model-editor]");
  const name = document.querySelector("[data-model-port-name]");
  const instruction = document.querySelector("[data-model-instruction]");
  const coordinates = document.querySelector("[data-model-coordinates]");
  const editStatus = document.querySelector("[data-model-status]");
  const saveButton = document.querySelector("[data-model-save]");
  const cancelButton = document.querySelector("[data-model-cancel]");
  const resetButton = document.querySelector("[data-model-reset]");
  const inventory = document.querySelector("[data-port-inventory]");
  const inventorySummary = document.querySelector("[data-port-inventory-summary]");
  const inventorySearch = document.querySelector("[data-port-inventory-search]");
  const missingList = document.querySelector("[data-port-missing-list]");
  const assignedList = document.querySelector("[data-port-assigned-list]");
  const inventoryTabs = [...document.querySelectorAll("[data-port-inventory-tab]")];
  const inventorySections = [...document.querySelectorAll("[data-port-inventory-section]")];
  const layer = L.featureGroup();
  const cityPins = new Map();
  const portsById = new Map();
  let pending = null, editToken = null, revision = 0, selected = null, preview = null, changed = false;
  let inventoryTab = "missing";
  map.createPane("portVerification").style.zIndex = 625;
  map.createPane("portVerificationHandles").style.zIndex = 650;

  function clearPreview() {
    if (preview) layer.removeLayer(preview);
    preview = null;
    changed = false;
    saveButton.disabled = true;
  }

  function clearSelection() {
    if (selected) cityPins.get(selected.id)?.getElement()?.classList.remove("is-selected");
    selected = null;
    editingActive = false;
    editor.hidden = true;
    clearPreview();
  }

  function setPreview(point, moved = true) {
    if (!point || !Number.isFinite(Number(point.latitude)) || !Number.isFinite(Number(point.longitude))) return;
    if (!preview) {
      preview = L.marker([point.latitude, point.longitude], {
        pane: "portVerificationHandles", draggable: true, autoPan: true, bubblingMouseEvents: false, zIndexOffset: 2000,
        title: "Drag model point to adjust it",
        icon: L.divIcon({ className: "port-edit-pin", html: "<span></span>", iconSize: [30, 30], iconAnchor: [15, 15] })
      }).addTo(layer);
      preview.on("dragend", () => {
        const location = preview.getLatLng();
        setPreview({ latitude: location.lat, longitude: location.lng });
      });
    } else preview.setLatLng([point.latitude, point.longitude]);
    changed = moved;
    saveButton.disabled = !changed;
    coordinates.textContent = `Model point: ${position(point)}${changed ? " · Unsaved" : ""}`;
    if (changed) editStatus.textContent = "Position ready. Save to keep it.";
  }

  function selectPort(port, { point = null, moved = false, skipPreview = false } = {}) {
    if (!editToken) return;
    clearSelection();
    selected = port;
    editingActive = true;
    editor.hidden = false;
    name.textContent = port.name;
    resetButton.hidden = !port.ownerModelPoint;
    cityPins.get(port.id)?.getElement()?.classList.add("is-selected");
    const current = point || port.proposed || port.sampling;
    if (current && !skipPreview) setPreview(current, moved);
    else coordinates.textContent = skipPreview ? "Drag the model pin to adjust it." : "No model point yet.";
    instruction.textContent = current ? "Drag the orange model pin, or tap water to place it elsewhere." : "Tap water to create a model point.";
    editStatus.textContent = moved ? "Position ready. Save to keep it." : "Location references are fixed. Only the model point can change.";
    map.closePopup();
  }

  function marker(port, point, kind) {
    const modelPin = kind === "sampling" || kind === "proposed";
    const classes = ["port-verification-pin", `port-verification-pin--${kind}`];
    if (kind === "port" && port.approved && !port.userSelectedRemote) classes.push("port-verification-pin--approved-city");
    if (kind === "sampling" && port.userSelectedRemote) classes.push("port-verification-pin--remote");
    const size = kind === "harbour" ? 46 : kind === "port" ? 24 : 30;
    const original = [point.latitude, point.longitude];
    const pin = L.marker(original, {
      pane: "portVerificationHandles", draggable: Boolean(editToken && modelPin), autoPan: true,
      bubblingMouseEvents: false,
      title: `${port.name} · ${modelPin ? "model point" : kind === "port" ? port.locationType === "bay" ? "bay reference" : "location reference" : kind === "harbour" ? "port reference" : "offshore target"}${editToken ? modelPin ? " · drag to move" : " · click to edit model point" : ""}`,
      icon: L.divIcon({ className: classes.join(" "), html: "<span></span>", iconSize: [size, size], iconAnchor: [size / 2, size / 2] })
    }).bindTooltip(`${port.name} · ${modelPin ? "model point" : kind === "port" ? port.locationType === "bay" ? "bay reference" : "location reference" : kind === "harbour" ? "port reference" : "offshore target"}`)
      .addTo(layer);
    pin.on("add", () => {
      const element = pin.getElement();
      if (element) { element.dataset.portId = port.id; element.dataset.pointKind = kind; }
    });
    if (editToken) {
      pin.on("click", () => selectPort(port, kind === "target" ? { point, moved: true } : {}));
      if (modelPin) {
        pin.on("dragstart", () => selectPort(port, { skipPreview: true }));
        pin.on("dragend", () => {
          const location = pin.getLatLng();
          pin.setLatLng(original);
          selectPort(port, { point: { latitude: location.lat, longitude: location.lng }, moved: true });
        });
      }
    } else pin.bindPopup(() => String(verificationPopup(port, kind)), { maxWidth: 280, className: "port-verification-balloon" });
    return pin;
  }

  function renderInventory(ports) {
    if (!inventory) return;
    const missing = ports.filter((port) => port.pointStatus === "missing");
    const assigned = ports.filter((port) => port.pointStatus !== "missing");
    inventorySummary.textContent = `Point inventory · ${missing.length} without · ${assigned.length} with`;
    document.querySelector("[data-port-missing-heading]").textContent = `Without a model point (${missing.length})`;
    document.querySelector("[data-port-assigned-heading]").textContent = `With a model point (${assigned.length})`;
    missingList.replaceChildren();
    assignedList.replaceChildren();
    for (const port of [...missing, ...assigned]) {
      const item = document.createElement("li");
      item.dataset.search = `${port.name} ${port.waterBody} ${port.region}`.toLocaleLowerCase();
      const button = document.createElement("button");
      button.type = "button";
      button.textContent = `${port.name} · ${port.locationType === "bay" ? "Bay · " : ""}${port.waterBody}`;
      button.addEventListener("click", () => {
        if (!toggle.checked) { toggle.checked = true; layer.addTo(map); }
        if (Number.isFinite(port.latitude) && Number.isFinite(port.longitude)) {
          map.flyTo([port.latitude, port.longitude], Math.max(map.getZoom(), 11));
        }
        selectPort(port);
      });
      item.append(button);
      const detail = document.createElement("small");
      if (port.pointStatus === "missing") detail.textContent = "No model point assigned";
      else {
        const point = port.sampling || port.proposed;
        const depth = port.pointDepth;
        const depthLabel = depth?.available
          ? `${Number(depth.depthMeters).toFixed(1)} m / ${Math.round(depth.depthMeters * 3.28084)} ft`
          : `Depth unavailable${depth?.reason ? `: ${depth.reason}` : ""}`;
        detail.textContent = `${port.pointStatus === "reviewed" ? "Reviewed" : "Proposed, unreviewed"} · ${depthLabel} · ${point ? position(point) : "Point unavailable"}${depth?.available ? ` · ${depth.source}` : ""}`;
      }
      item.append(detail);
      (port.pointStatus === "missing" ? missingList : assignedList).append(item);
    }
    applyInventoryFilter();
  }

  function applyInventoryFilter() {
    if (!inventory) return;
    const query = inventorySearch.value.trim().toLocaleLowerCase();
    for (const item of inventory.querySelectorAll("li")) item.hidden = !item.dataset.search.includes(query);
    for (const section of inventorySections) {
      const isTab = section.dataset.portInventorySection === inventoryTab;
      const hasMatches = [...section.querySelectorAll("li")].some((item) => !item.hidden);
      section.hidden = query ? !hasMatches : !isTab;
    }
    for (const tab of inventoryTabs) {
      tab.setAttribute("aria-pressed", String(tab.dataset.portInventoryTab === inventoryTab));
    }
  }

  async function load() {
    const response = await fetch("/admin/port-map/data", { credentials: "same-origin", signal: AbortSignal.timeout(15000) });
    if (!response.ok) throw new Error("Location points unavailable");
    const data = await response.json();
    clearSelection();
    layer.clearLayers();
    cityPins.clear();
    portsById.clear();
    editToken = data.canEdit ? data.editToken : null;
    revision = data.revision;
    for (const port of [...data.ports, ...(data.bays || [])]) {
      portsById.set(port.id, port);
      if (port.approved) {
        L.polyline([[port.latitude, port.longitude], [port.target.latitude, port.target.longitude], [port.sampling.latitude, port.sampling.longitude]], {
          pane: "portVerification", color: port.userSelectedRemote ? "#bba3ff" : "#55d8db", weight: 2, dashArray: "5 4", interactive: false
        }).addTo(layer);
        if (!port.userSelectedRemote) marker(port, port.target, "target");
        marker(port, port.sampling, "sampling");
      } else if (port.proposed) {
        L.polyline([[port.latitude, port.longitude], [port.proposed.latitude, port.proposed.longitude]], {
          pane: "portVerification", color: "#bba3ff", weight: 2, dashArray: "2 6", interactive: false
        }).addTo(layer);
        marker(port, port.proposed, "proposed");
      }
      if (port.harbour) marker(port, port.harbour, "harbour");
      cityPins.set(port.id, marker(port, port, "port"));
    }
    renderInventory([...data.ports, ...(data.bays || [])]);
    status.hidden = true;
  }

  async function update() {
    if (!toggle.checked) { clearSelection(); map.removeLayer(layer); return; }
    status.hidden = false;
    status.textContent = "Loading locations…";
    try {
      pending ??= load();
      await pending;
      if (toggle.checked) layer.addTo(map);
    } catch (error) {
      status.hidden = false;
      status.textContent = error.message;
    } finally { pending = null; }
  }

  toggle.addEventListener("change", update);
  inventorySearch?.addEventListener("input", applyInventoryFilter);
  for (const tab of inventoryTabs) tab.addEventListener("click", () => {
    inventoryTab = tab.dataset.portInventoryTab;
    applyInventoryFilter();
  });
  map.on("click", (event) => {
    if (!editingActive) return;
    setPreview({ latitude: event.latlng.lat, longitude: event.latlng.lng });
  });
  cancelButton.addEventListener("click", clearSelection);

  async function submit(kind, point = null) {
    const portId = selected.id;
    const response = await fetch("/admin/port-map/edit", {
      method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json", "X-CSRF-Token": editToken },
      body: JSON.stringify({ id: portId, kind, point, revision }), signal: AbortSignal.timeout(15000)
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.error || "Could not save the model point");
    await load();
    selectPort(portsById.get(portId));
    editStatus.textContent = kind === "reset" ? "Original model point restored." : "Model point saved as a proposal. Data coverage still needs verification.";
  }

  saveButton.addEventListener("click", async () => {
    if (!editingActive || !changed || !preview) return;
    saveButton.disabled = true;
    editStatus.textContent = "Saving…";
    const location = preview.getLatLng();
    try { await submit("model", { latitude: location.lat, longitude: location.lng }); }
    catch (error) { editStatus.textContent = error.message; saveButton.disabled = false; }
  });
  resetButton.addEventListener("click", async () => {
    if (!editingActive) return;
    resetButton.disabled = true;
    try { await submit("reset"); }
    catch (error) { editStatus.textContent = error.message; resetButton.disabled = false; }
  });

  toggle.checked = true;
  update();
}
